// TriageVerdict (#830, epic #828) — the deterministic triage gate's one-shot
// judgment over a dispatch candidate, plus the audit-row record that attests it.
//
// One shape, every lane: the dedup-as-gate in `createTaskDispatcher.dispatch`
// (#830) emits `dupe-of` today; #833 lands the full gate (schedule / escalate /
// drop-noise) against THESE types, and #829's error-lane triage reuses them.
// "Near `task-envelope.ts`" by spec: the envelope is what gets judged, so the
// verdict lives beside it. snake_case fields — the record crosses into
// `audit_events.output` JSON verbatim, same convention as the envelope.
//
// Verdict semantics (locked 2026-07-21):
//   schedule   — dispatch now (HIGH-confidence, dedup-cleared; #833 auto-schedule).
//   escalate   — a human decides (low confidence / unresolved ambiguity).
//   drop-noise — not real work (spam / noise); dropped, never dispatched.
//   dupe-of    — an IN-MOTION task already covers this (same repo, active state);
//                cross-linked to that task and dropped. Finished work never gates.
//   needs_info — the ASKER can make this decidable and has not yet (plan 101
//                slice 08): the questions go to them, the ask parks in
//                `awaiting_info`, and their answer re-enters triage. Distinct from
//                `escalate`, which is for what only a maintainer can decide. A raw
//                task verdict: the envelope-level gate (`createTaskDispatcher`) and
//                the board scheduler never emit it — they judge work already
//                scoped, and nobody is left to ask.

import { z } from 'zod';
import { TaskIdSchema } from './ids';
import type { TaskEnvelope } from './task-envelope';

export const TriageVerdict = {
  Schedule: 'schedule',
  Escalate: 'escalate',
  DropNoise: 'drop-noise',
  DupeOf: 'dupe-of',
  NeedsInfo: 'needs_info',
} as const;
export const TriageVerdictSchema = z.enum(TriageVerdict);
export type TriageVerdict = z.infer<typeof TriageVerdictSchema>;

/**
 * The `triage.verdict` audit-row record (#830) — one row per judged dispatch
 * candidate, carrying the verdict plus the evidence that produced it. Optional
 * evidence fields stay absent (not null) when they don't apply, so a row never
 * claims a number it didn't compute. `reason` is the short machine-readable
 * basis (`similar_in_flight`, `below_confidence`, `noise`, `auto_schedule`…) —
 * free text would defeat log aggregation.
 *
 * Cross-link invariants (the `superRefine` below): `dupe-of` REQUIRES
 * `dupe_of_task_id` — without it the dropped candidate has no join to the
 * surviving work — and the flat `similarity_*` evidence is `dupe-of`-only; a
 * non-dupe verdict carries its lookup context in the row's decision context,
 * never in fields that assert a cleared score.
 */
export const TriageVerdictRecordSchema = z
  .object({
    verdict: TriageVerdictSchema,
    /** The envelope's confidence at decision time. */
    confidence: z.number().min(0).max(1),
    /** Short machine-readable basis for the verdict (see top comment). */
    reason: z.string().min(1).max(200),
    /**
     * `dupe-of` cross-link: the in-motion task this candidate duplicates. The
     * link IS the cross-link — the dropped candidate mints no row of its own,
     * so the trail's pointer to the surviving task is the only join.
     */
    dupe_of_task_id: TaskIdSchema.optional(),
    /** The matched task's linked OPEN PR, when it has one — the human-followable half of the cross-link. */
    dupe_of_pr: z.number().int().positive().optional(),
    /** Winning similarity score (0–1) when the dedup gate matched. */
    similarity_score: z.number().min(0).max(1).optional(),
    /** The policy `triage.dedupe.similarity_threshold` the score cleared. */
    similarity_threshold: z.number().min(0).max(1).optional(),
  })
  .superRefine((r, ctx) => {
    // Cross-link invariants: a `dupe-of` row without `dupe_of_task_id` drops
    // the candidate with no join to the surviving work, and similarity
    // evidence on a non-dupe verdict claims a comparison the row didn't make
    // (a cleared dedup lookup attests its threshold via the row's decision
    // context instead — see the dispatcher's no-match row). Keep malformed
    // rows out of the append-only log: there is no fixing them later.
    if (r.verdict === TriageVerdict.DupeOf && r.dupe_of_task_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'dupe-of verdicts require dupe_of_task_id',
        path: ['dupe_of_task_id'],
      });
    }
    if (r.verdict !== TriageVerdict.DupeOf) {
      if (r.similarity_score !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `similarity_score is only valid on a '${TriageVerdict.DupeOf}' verdict`,
          path: ['similarity_score'],
        });
      }
      if (r.similarity_threshold !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `similarity_threshold is only valid on a '${TriageVerdict.DupeOf}' verdict`,
          path: ['similarity_threshold'],
        });
      }
      if (r.dupe_of_pr !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `dupe_of_pr is only valid on a '${TriageVerdict.DupeOf}' verdict`,
          path: ['dupe_of_pr'],
        });
      }
    }
  });
export type TriageVerdictRecord = z.infer<typeof TriageVerdictRecordSchema>;

/**
 * THE text the triage similarity machinery judges — `title` + `problem`,
 * newline-joined. Single source: the task-write embedding path and the
 * query-time embed/trigram path MUST compose the identical string, or cosine
 * compares two different documents and the blend silently degrades.
 */
export function triageText(e: Pick<TaskEnvelope, 'title' | 'problem'>): string {
  return `${e.title}\n${e.problem}`;
}
